import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core';
import {
  SegmentedTab,
  SegmentedTabs,
} from '@src/components/Common/SegmentedTabs';
import IconWrapper from '@src/components/Common/IconWrapper';
import {
  chatsOnBotFocused,
  chatsOnHoldFocused,
  chatsRequestFocused,
  fetchChatSideTotals,
} from '@src/reducers/chats';
import { showWarningTraysOnHold } from '@src/selectors/chatAutoAssignment';
import {
  activeChatSideSelector,
  totalCallsBotSelector,
  totalCallsOnHoldSelector,
  totalCallsRequestSelector,
} from '@src/selectors/chats';
import { getConsolePreferenceShowBotsInbox } from '@src/selectors/preferences';
import { ChatSideType } from '@src/types/chats';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import BadgeCountBlockedWrapper from './BadgeCountBlocked';
import { BadgeCountMsgs } from './BadgeCountMsgs';
import ChatTrayCallBadge from './ChatTrayCallBadge';
import { BotChatsIcon, OngoingChatsIcon, RequestChatsIcon } from './SvgIcons';

export const ChatTraySelectionTabs = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const showBotsInbox = useSelector(getConsolePreferenceShowBotsInbox);
  const totalCallsOnHold = useSelector(totalCallsOnHoldSelector);
  const totalCallsRequest = useSelector(totalCallsRequestSelector);
  const totalCallsBot = useSelector(totalCallsBotSelector);

  const activeChatSide = useSelector(activeChatSideSelector);
  const warningTraysOnHold = useSelector(showWarningTraysOnHold);
  const theme = useTheme();
  const hasWarning =
    warningTraysOnHold && activeChatSide === ChatSideType.REQUEST;
  const tabStyles = useStyles();

  const onChangeActiveChatSide = (newChatSide: ChatSideType) => {
    if (newChatSide === ChatSideType.REQUEST) {
      dispatch(chatsRequestFocused());
      dispatch(fetchChatSideTotals(ChatSideType.REQUEST));
    } else if (newChatSide === ChatSideType.ON_HOLD) {
      dispatch(chatsOnHoldFocused());
      dispatch(fetchChatSideTotals(ChatSideType.ON_HOLD));
    } else if (newChatSide === ChatSideType.BOT) {
      dispatch(chatsOnBotFocused());
      dispatch(fetchChatSideTotals(ChatSideType.BOT));
    }
  };

  const getClasses = (type: ChatSideType) =>
    `${tabStyles.tabTextContainer} ${
      activeChatSide === type ? tabStyles.selectedColor : tabStyles.color
    }`;

  const icons = {
    [ChatSideType.ON_HOLD]: OngoingChatsIcon,
    [ChatSideType.REQUEST]: RequestChatsIcon,
    [ChatSideType.BOT]: BotChatsIcon,
  };

  const getTrayIcon = (type: ChatSideType) => (
    <IconWrapper fontSize="1.3rem">{icons[type]}</IconWrapper>
  );

  return (
    <Box px={0.5}>
      <SegmentedTabs
        variant="standard"
        value={activeChatSide}
        onChange={(_, chatSide) => onChangeActiveChatSide(chatSide)}
        className={tabStyles.tabs}
        TabIndicatorProps={{
          style: {
            backgroundColor: hasWarning
              ? theme.b2chatorange[500]
              : theme.palette.primary.main,
          },
        }}
      >
        <SegmentedTab
          key={1}
          value={ChatSideType.ON_HOLD}
          className={tabStyles.tabWidth}
          label={
            <ChatTrayCallBadge
              count={totalCallsOnHold}
              className={tabStyles.callBadge}
            >
              <Box className={tabStyles.container}>
                <Box className={getClasses(ChatSideType.ON_HOLD)}>
                  <BadgeCountMsgs type={ChatSideType.ON_HOLD}>
                    {getTrayIcon(ChatSideType.ON_HOLD)}
                  </BadgeCountMsgs>
                  <Box className={tabStyles.text} data-testid="chat-tab-onhold">
                    {formatMessage({ id: 'ChatsOngoing.title' })}
                  </Box>
                </Box>
              </Box>
            </ChatTrayCallBadge>
          }
        />

        <SegmentedTab
          key={2}
          value={ChatSideType.REQUEST}
          className={tabStyles.tabWidth}
          label={
            <ChatTrayCallBadge
              count={totalCallsRequest}
              className={tabStyles.callBadge}
            >
              <Box className={tabStyles.container}>
                <Box
                  className={getClasses(ChatSideType.REQUEST)}
                  color="inherit"
                >
                  {warningTraysOnHold ? (
                    <BadgeCountBlockedWrapper>
                      {getTrayIcon(ChatSideType.REQUEST)}
                    </BadgeCountBlockedWrapper>
                  ) : (
                    <BadgeCountMsgs type={ChatSideType.REQUEST}>
                      {getTrayIcon(ChatSideType.REQUEST)}
                    </BadgeCountMsgs>
                  )}
                  <Box
                    className={tabStyles.text}
                    data-testid="chat-tab-request"
                  >
                    {formatMessage({ id: 'ChatRequests.title' })}
                  </Box>
                </Box>
              </Box>
            </ChatTrayCallBadge>
          }
        />
        {showBotsInbox && (
          <SegmentedTab
            key={3}
            value={ChatSideType.BOT}
            className={tabStyles.tabBotWidth}
            label={
              <ChatTrayCallBadge
                count={totalCallsBot}
                className={tabStyles.callBadge}
              >
                <Box className={tabStyles.container}>
                  <Box className={getClasses(ChatSideType.BOT)}>
                    <BadgeCountMsgs type={ChatSideType.BOT}>
                      {getTrayIcon(ChatSideType.BOT)}
                    </BadgeCountMsgs>
                    <Box className={tabStyles.text} data-testid="chat-tab-bot">
                      {formatMessage({ id: 'ChatsBotState.title' })}
                    </Box>
                  </Box>
                </Box>
              </ChatTrayCallBadge>
            }
          />
        )}
      </SegmentedTabs>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Container background, rounded top corners and the indicator come from the
    // shared SegmentedTabs/SegmentedTab; only the inbox-specific bits (taller
    // rows for the tray icons and visible overflow for the count badges) live
    // here.
    tabs: {
      '& .MuiTab-root': {
        textAlign: 'center',
        maxHeight: '55px !important',
        minHeight: '55px !important',
        height: '50px !important',
      },
      '& .MuiTab-textColorInherit': {
        opacity: 1,
      },

      '& .MuiTabs-scroller': {
        overflow: 'visible !important',
      },
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '4px 0 0 0',
    },

    callBadge: {
      top: -6,
      right: -22,
      backgroundColor: theme.b2chatgreen['300'],
      color: '#FFF',
      fontSize: '.9rem',
      fontWeight: 600,
      padding: 5,
    },

    tabTextContainer: {
      fontWeight: 400,
      whiteSpace: 'nowrap',
    },

    text: {
      fontWeight: 500,
      lineHeight: '130%',
    },

    tabWidth: {
      minWidth: '100px !important',
      width: '100px !important',
    },

    tabBotWidth: {
      minWidth: '92px !important',
      width: '92px !important',
      maxWidth: '92px !important',
    },

    selectedColor: {
      color: theme.palette.primary.main,
    },

    color: {
      color: theme.palette.text.secondary,
    },
  })
);

export default ChatTraySelectionTabs;
